import { PlusOutlined } from '@ant-design/icons';
import { Button, Flex, message, Popconfirm, Select, Space, Table } from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import React, { useCallback, useMemo, useState } from 'react';
import { useClientsServiceGetBackendClients, useOutputFormatsServiceDeleteBackendOutputformats, useOutputFormatsServiceGetBackendOutputformats } from '../../../backendapi/queries';
import { OutputFormatDTO } from '../../../backendapi/requests';
import { GenerateODataFilter } from '../../../helpers/odataFunctions';
import { Filter, FilterGroup } from '../../../types/types';
import CreateOutputFormat from './CreateOutputFormat';
import EditOutputFormat from './EditOutputFormat';

const OutputFormatList: React.FC = () => {
    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(1);
    const [sortField, setSortField] = useState<string>("id");
    const [sortOrder, setSortOrder] = useState<"desc" | "asc">("desc");
    const [selectedClients, setSelectedClients] = useState<number[]>([]);
    const { data: clients } = useClientsServiceGetBackendClients({});
    const { mutateAsync: deleteFormat } = useOutputFormatsServiceDeleteBackendOutputformats();

    const filters = useMemo(() => {
        let filters: (Filter | FilterGroup)[] = [];
        if (selectedClients.length > 0) {
            filters.push({ type: 'select', values: selectedClients.map(e => ({ value: e })), property: 'clientId', name: 'clientId' });
        }

        return filters;
    }, [selectedClients]);

    const { data, isLoading, refetch } = useOutputFormatsServiceGetBackendOutputformats({
        top: pageSize.toString(),
        skip: ((page - 1) * pageSize).toString(),
        filter: GenerateODataFilter(filters),
        orderby: `${sortField} ${sortOrder}`,
        count: 'true'
    });

    const [selectedOutputFormat, setSelectedOutputFormat] = useState<OutputFormatDTO | undefined>(undefined);

    const [createOpen, setCreateOpen] = useState(false);

    const openCreate = () => {
        setCreateOpen(true);
    };

    const handleCreateClose = useCallback(() => {
        setCreateOpen(false);
        refetch();
    }, [refetch]);

    const handleEditClose = useCallback(() => {
        setSelectedOutputFormat(undefined);
        refetch();
    }, [refetch]);

    const handleTableChange = useCallback((pagination: TablePaginationConfig, filters: Record<string, FilterValue | null>, sorter: SorterResult<OutputFormatDTO> | SorterResult<OutputFormatDTO>[]) => {
        setPage(pagination.current ?? 1);
        setPageSize(pagination.pageSize ?? 10);

        if (!Array.isArray(sorter)) {
            setSortField(sorter.field?.toString() ?? "id");
            setSortOrder(sorter.order === 'ascend' ? 'asc' : 'desc');
        }
    }, []);

    const handleClientChange = useCallback((value: any, option: { label: string; value: string; } | { label: string; value: string; }[]) => {
        if (Array.isArray(option)) {
            setSelectedClients(option.map(e => parseInt(e.value)));
        }
    }, []);

    const handleDelete = useCallback((format: OutputFormatDTO) => {
        if (format.id) {
            deleteFormat({ key: format.id }).then(() => {
                message.success("Format deleted");
                refetch();
            })
        }
    }, [deleteFormat, refetch]);

    const columns: ColumnsType<OutputFormatDTO> = [
        {
            title: 'Id',
            dataIndex: 'id',
            key: 'id',
            sorter: (a, b) => a.id - b.id,
        },
        {
            title: 'Client',
            dataIndex: 'client',
            key: 'clientId',
            render: (_, record) => (
                clients?.value.find(e => e.id === record.clientId)?.name
            ),
        },
        {
            title: 'Title',
            dataIndex: 'title',
            key: 'title',
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => (
                <Space size="middle">
                    <Space size="middle">
                        <Button type="link" onClick={() => setSelectedOutputFormat(record)}>Edit</Button>
                    </Space>
                    <Popconfirm title="Sure to delete?" onConfirm={() => handleDelete(record)}>
                        <Button type="link">Delete</Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <>
            <Flex justify='space-between' style={{ marginBottom: 16 }}>

                <Space>
                    <Select
                        mode="multiple"
                        placeholder="Client"
                        onChange={handleClientChange}
                        popupMatchSelectWidth={false}
                        size='middle'
                        style={{ minWidth: 100 }}
                        options={clients?.value.map(e => ({ label: e.name, value: e.id.toString() }))}
                    />
                </Space>

                <Space>
                    <Button type="primary" onClick={openCreate} icon={<PlusOutlined />} style={{ marginBottom: 16 }}>
                        New configuration
                    </Button>
                </Space>
            </Flex>


            <CreateOutputFormat isOpen={createOpen} onClose={handleCreateClose} />

            <EditOutputFormat isOpen={selectedOutputFormat != null} onClose={handleEditClose} outputFormat={selectedOutputFormat} />

            <Table
                loading={isLoading}
                dataSource={data?.value ?? Array.from({ length: pageSize }).map(x => ({}))}
                columns={columns}
                rowKey={"id"}
                onChange={handleTableChange}
                pagination={{
                    total: (data !== undefined && data['@odata.count']) ? data['@odata.count'] : 0,
                    current: page,
                    pageSize: pageSize,
                }} />
        </>
    );
};

export default OutputFormatList;